import { motion } from "framer-motion";
import { CheckCircle2, RotateCcw } from "lucide-react";
import { ConnectionPill } from "@/components/portal/ConnectionPill";
import { ProgressBar } from "@/components/admin/RedeemProgress";

export const ConfirmationCard = ({ title, content, protocolo, children }) => (
  <div className="glass-card p-8 sm:p-10" data-testid="confirmation-card">
    <CheckCircle2 className="h-12 w-12" style={{ color: "var(--brand)" }} />
    <h1 className="mt-6 font-display text-3xl text-slate-50 sm:text-4xl" data-testid="confirmation-title">{title}</h1>
    <p className="mt-5 whitespace-pre-line text-base leading-relaxed text-slate-300 md:text-lg" data-testid="confirmation-content">{content}</p>
    {protocolo && (
      <p className="mt-8 text-sm text-slate-400">Protocolo <span className="font-mono-x text-slate-100" data-testid="confirmation-protocol">{protocolo}</span></p>
    )}
    {children}
  </div>
);

export const ConfirmationStep = ({ state, onReset }) => (
  <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-2xl" data-testid="confirmation-step">
    <ConfirmationCard title={state.confirmation?.title} content={state.confirmation?.content} protocolo={state.protocolo}>
      <div className="mt-6 grid gap-3 border-t border-white/10 pt-6 text-sm text-slate-400 sm:grid-cols-2">
        <span>Responsável: <span className="text-slate-200">{state.appointment?.nome}</span></span>
        <span>Horário: <span className="text-slate-200">{state.appointment?.horario_label}</span></span>
        <span>{state.tipo === "PF" ? "CPF" : "CNPJ"}: <span className="font-mono-x text-slate-200">{state.cnpj_fmt}</span></span>
        <span>Agência: <span className="font-mono-x text-slate-200">{state.appointment?.agencia}</span></span>
      </div>
    </ConfirmationCard>
    <button onClick={onReset} className="mt-6 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200" data-testid="new-request-btn">
      <RotateCcw className="h-4 w-4" /> Iniciar nova consulta
    </button>
  </motion.div>
);

export const RedeemRequestedStep = ({ state, connection }) => (
  <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="glass-card w-full max-w-xl p-8 text-center sm:p-10" data-testid="redeem-requested-step">
    <motion.div className="mx-auto h-16 w-16 rounded-full" style={{ background: "radial-gradient(circle, var(--brand) 0%, transparent 70%)" }} animate={{ scale: [1, 1.15, 1], opacity: [0.7, 1, 0.7] }} transition={{ duration: 2, repeat: Infinity }} />
    <h1 className="mt-6 font-display text-2xl text-slate-50 sm:text-3xl">Resgate solicitado</h1>
    <div className="mx-auto mt-6 max-w-sm text-left"><ProgressBar value={state.redeem_progress || 0} stages={state.progress_stages} testId="redeem-progress" /></div>
    <p className="mt-6 text-slate-300" data-testid="redeem-requested-message">Recebemos seu pedido de resgate. O seu resgate está em andamento. Aguarde um instante.</p>
    <p className="mt-6 text-sm text-slate-400">Protocolo <span className="font-mono-x text-slate-200">{state.protocolo}</span></p>
    <div className="mt-4 flex justify-center"><ConnectionPill status={connection} /></div>
  </motion.div>
);

export const RedeemCompletedStep = ({ state, onReset }) => (
  <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-2xl" data-testid="redeem-completed-step">
    <ConfirmationCard title={state.redeem_completion?.title} content={state.redeem_completion?.content} protocolo={state.protocolo} />
    <button onClick={onReset} className="mt-6 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200" data-testid="redeem-new-request-btn">
      <RotateCcw className="h-4 w-4" /> Iniciar nova consulta
    </button>
  </motion.div>
);

export const UnavailableStep = ({ state, onReset }) => (
  <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="glass-card w-full max-w-xl p-8 sm:p-10" data-testid="unavailable-step">
    <h1 className="font-display text-2xl text-slate-50 sm:text-3xl">Informações indisponíveis no momento</h1>
    <p className="mt-4 text-slate-300" data-testid="unavailable-message">
      Não foi possível disponibilizar as informações de pontuação para o {state.tipo === "PF" ? "CPF" : "CNPJ"} {state.cnpj_fmt} neste momento. Tente novamente mais tarde.
    </p>
    <p className="mt-6 text-sm text-slate-400">Protocolo <span className="font-mono-x text-slate-200">{state.protocolo}</span></p>
    <button onClick={onReset} className="btn-ghost mt-8" data-testid="unavailable-new-request-btn"><RotateCcw className="h-4 w-4" /> Nova consulta</button>
  </motion.div>
);
